"use client"

import { AuthGuard } from '@/components/auth-guard'
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import {
  IconTrendingUp,
  IconTrendingDown,
  IconUsers,
  IconTarget,
  IconDeviceGamepad2,
  IconClock,
  IconActivity,
  IconDownload,
  IconLoader2,
  IconFileText
} from "@tabler/icons-react"
import { ChartAreaInteractive } from "@/components/chart-area-interactive"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from "@/components/ui/chart"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts"
import { callApi } from "@/lib/api-client"

interface MetricCard {
  title: string
  value: string | number
  change: string
  trend: "up" | "down" | "neutral"
  icon: React.ReactNode
}

interface TopPerformer {
  name: string
  value: string
  change: string
  type: string
}

interface DailyAnalytics {
  date: string
  qr_scans: number
  unique_users: number
  game_sessions: number
  games_completed: number
  engagement_rate: number
  retention_rate: number
  conversion_rate: number
  avg_session_duration: number
}

export default function AnalyticsPage() {
  return (
    <AuthGuard>
      <AnalyticsPageContent />
    </AuthGuard>
  )
}

interface HourlyData {
  hour: number
  sessions: number
  unique_users: number
  completed: number
}

const hourlyChartConfig = {
  sessions: {
    label: "Sessions",
    color: "#f97316",
  },
  unique_users: {
    label: "Players",
    color: "#06b6d4",
  },
} satisfies ChartConfig

function AnalyticsPageContent() {
  const [timeRange, setTimeRange] = useState("30d")
  const [activeTab, setActiveTab] = useState("overview")
  const [loading, setLoading] = useState(true)
  const [analytics, setAnalytics] = useState<DailyAnalytics[]>([])
  const [campaigns, setCampaigns] = useState<any[]>([])
  const [hourlyData, setHourlyData] = useState<HourlyData[]>([])
  const [mounted, setMounted] = useState(false)

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  // Fetch analytics data on mount and time range change
  useEffect(() => {
    if (!mounted) return
    fetchAnalyticsData()
  }, [timeRange, mounted])

  async function fetchAnalyticsData() {
    try {
      setLoading(true)

      // Calculate date range
      const days = parseInt(timeRange.replace('d', '')) || 30
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(endDate.getDate() - days)

      // Fetch daily analytics from new dashboard endpoint
      const analyticsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1'}/dashboard.analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify({
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        })
      }).then(res => res.json()).then(data => data.success ? data.data : [])

      // Fetch hourly analytics
      const hourlyResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1'}/analytics.hourly`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify({
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        })
      }).then(res => res.json()).then(data => data.success ? data.data : [])

      // Fetch campaigns for top performers
      const campaignsData = await callApi('qr_campaigns', 'list', {})

      // Ensure analytics is an array
      const analyticsArray = Array.isArray(analyticsResponse) ? analyticsResponse : []
      const hourlyArray = Array.isArray(hourlyResponse) ? hourlyResponse : []

      setAnalytics(analyticsArray)
      setHourlyData(hourlyArray)
      setCampaigns(Array.isArray(campaignsData) ? campaignsData : [])
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  // Calculate totals from analytics data
  const totals = (analytics || []).reduce((acc: any, day: any) => ({
    qrScans: acc.qrScans + (day.qr_scans || 0),
    uniqueUsers: acc.uniqueUsers + (day.unique_users || 0),
    gameSessions: acc.gameSessions + (day.game_sessions || 0),
    avgEngagementRate: acc.avgEngagementRate + (day.engagement_rate || 0),
    avgRetentionRate: acc.avgRetentionRate + (day.retention_rate || 0),
    avgConversionRate: acc.avgConversionRate + (day.conversion_rate || 0),
    avgSessionDuration: acc.avgSessionDuration + (day.avg_session_duration || 0),
  }), {
    qrScans: 0,
    uniqueUsers: 0,
    gameSessions: 0,
    avgEngagementRate: 0,
    avgRetentionRate: 0,
    avgConversionRate: 0,
    avgSessionDuration: 0,
  })

  // Calculate averages
  const daysCount = analytics.length || 1
  const avgEngagementRate = totals.avgEngagementRate / daysCount
  const avgRetentionRate = totals.avgRetentionRate / daysCount
  const avgConversionRate = totals.avgConversionRate / daysCount
  const avgSessionDurationSeconds = totals.avgSessionDuration / daysCount

  // Format session duration
  function formatDuration(seconds: number): string {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  }

  // Format large numbers
  function formatNumber(num: number): string {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toLocaleString()
  }

  // Export functions
  function exportToCSV() {
    if (analytics.length === 0 && hourlyData.length === 0) {
      alert('No data available to export')
      return
    }

    const rows: string[] = []

    // Summary section
    rows.push('ANALYTICS REPORT')
    rows.push(`Time Range: ${timeRange}`)
    rows.push(`Generated: ${new Date().toLocaleString()}`)
    rows.push('')

    // Metrics summary
    rows.push('METRICS SUMMARY')
    rows.push('Metric,Value')
    rows.push(`Total QR Scans,${totals.qrScans}`)
    rows.push(`Active Players,${totals.uniqueUsers}`)
    rows.push(`Games Played,${totals.gameSessions}`)
    rows.push(`Avg Engagement Rate,${avgEngagementRate.toFixed(1)}%`)
    rows.push(`Avg Session Duration,${formatDuration(Math.round(avgSessionDurationSeconds))}`)
    rows.push(`Avg Retention Rate,${avgRetentionRate.toFixed(1)}%`)
    rows.push(`Conversion Rate,${avgConversionRate.toFixed(1)}%`)
    rows.push('')

    // Daily analytics
    if (analytics.length > 0) {
      rows.push('DAILY ANALYTICS')
      rows.push('Date,QR Scans,Unique Users,Game Sessions,Games Completed,Engagement Rate,Retention Rate,Conversion Rate,Avg Session Duration')
      analytics.forEach(day => {
        rows.push([
          day.date,
          day.qr_scans || 0,
          day.unique_users || 0,
          day.game_sessions || 0,
          day.games_completed || 0,
          `${(day.engagement_rate || 0).toFixed(1)}%`,
          `${(day.retention_rate || 0).toFixed(1)}%`,
          `${(day.conversion_rate || 0).toFixed(1)}%`,
          formatDuration(day.avg_session_duration || 0)
        ].join(','))
      })
      rows.push('')
    }

    // Hourly analytics
    if (hourlyData.length > 0) {
      rows.push('HOURLY ACTIVITY')
      rows.push('Hour,Sessions,Unique Players,Completed')
      hourlyData.forEach(hour => {
        rows.push([
          formatHour(hour.hour),
          hour.sessions || 0,
          hour.unique_users || 0,
          hour.completed || 0
        ].join(','))
      })
      rows.push('')
    }

    // Top campaigns
    if (campaigns.length > 0) {
      rows.push('TOP CAMPAIGNS')
      rows.push('Campaign Name,Total Scans,Status,Unique Scans,Participants')
      campaigns.slice(0, 10).forEach(campaign => {
        rows.push([
          `"${campaign.name || 'Unnamed'}"`,
          campaign.total_scans || 0,
          campaign.status || 'Unknown',
          campaign.unique_scans || 0,
          campaign.total_participants || 0
        ].join(','))
      })
    }

    // Create and download the CSV file
    const csvContent = rows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `analytics_report_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  function exportToJSON() {
    if (analytics.length === 0 && hourlyData.length === 0) {
      alert('No data available to export')
      return
    }

    const report = {
      metadata: {
        timeRange: timeRange,
        generatedAt: new Date().toISOString(),
        daysOfData: daysCount
      },
      summary: {
        totalQrScans: totals.qrScans,
        activePlayers: totals.uniqueUsers,
        gamesPlayed: totals.gameSessions,
        avgEngagementRate: avgEngagementRate,
        avgRetentionRate: avgRetentionRate,
        avgConversionRate: avgConversionRate,
        avgSessionDuration: avgSessionDurationSeconds
      },
      dailyAnalytics: analytics,
      hourlyActivity: hourlyData,
      topCampaigns: campaigns.slice(0, 10).map(c => ({
        name: c.name,
        totalScans: c.total_scans,
        uniqueScans: c.unique_scans,
        totalParticipants: c.total_participants,
        status: c.status
      }))
    }

    const jsonContent = JSON.stringify(report, null, 2)
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `analytics_report_${new Date().toISOString().split('T')[0]}.json`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Metric cards using real data
  const metricCards: MetricCard[] = [
    {
      title: "Total QR Scans",
      value: loading ? '...' : formatNumber(totals.qrScans),
      change: "+28.4%", // TODO: Calculate from previous period
      trend: "up",
      icon: <IconTarget className="h-4 w-4 text-muted-foreground" />
    },
    {
      title: "Active Players",
      value: loading ? '...' : formatNumber(totals.uniqueUsers),
      change: "+15.6%", // TODO: Calculate from previous period
      trend: "up",
      icon: <IconUsers className="h-4 w-4 text-muted-foreground" />
    },
    {
      title: "Games Played",
      value: loading ? '...' : formatNumber(totals.gameSessions),
      change: "+42.1%", // TODO: Calculate from previous period
      trend: "up",
      icon: <IconDeviceGamepad2 className="h-4 w-4 text-muted-foreground" />
    },
    {
      title: "Conversion Rate",
      value: loading ? '...' : `${avgConversionRate.toFixed(1)}%`,
      change: "+5.2%", // TODO: Calculate from previous period
      trend: "up",
      icon: <IconActivity className="h-4 w-4 text-muted-foreground" />
    },
    {
      title: "Avg. Session Duration",
      value: loading ? '...' : formatDuration(Math.round(avgSessionDurationSeconds)),
      change: "-8.3%", // TODO: Calculate from previous period
      trend: "down",
      icon: <IconClock className="h-4 w-4 text-muted-foreground" />
    },
    {
      title: "Engagement Rate",
      value: loading ? '...' : `${avgEngagementRate.toFixed(1)}%`,
      change: "+12.5%", // TODO: Calculate from previous period
      trend: "up",
      icon: <IconActivity className="h-4 w-4 text-muted-foreground" />
    }
  ]

  // Top campaigns by scans
  const topCampaigns: TopPerformer[] = campaigns
    .sort((a, b) => (b.total_scans || 0) - (a.total_scans || 0))
    .slice(0, 5)
    .map(campaign => ({
      name: campaign.name || 'Unnamed Campaign',
      value: `${formatNumber(campaign.total_scans || 0)} scans`,
      change: "+N/A", // No historical comparison yet
      type: "Campaign"
    }))

  // Format hour for display (24-hour clock: 0=12AM, 12=12PM, etc.)
  function formatHour(hour: number): string {
    if (hour === 0) return "12 AM"
    if (hour === 12) return "12 PM"
    if (hour < 12) return `${hour} AM`
    return `${hour - 12} PM`
  }

  return (
    <div className="flex-1 space-y-4 p-4 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Analytics</h2>
        <div className="flex items-center space-x-2">
          <Select value={timeRange} onValueChange={setTimeRange} disabled={loading}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading || analytics.length === 0}>
                <IconDownload className="mr-2 h-4 w-4" />
                Export Report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToCSV}>
                <IconFileText className="mr-2 h-4 w-4" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON}>
                <IconFileText className="mr-2 h-4 w-4" />
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Metric Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {metricCards.map((card, index) => (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
                  {loading ? <IconLoader2 className="h-4 w-4 animate-spin" /> : card.icon}
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{card.value}</div>
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    {card.trend === "up" ? (
                      <IconTrendingUp className="h-3 w-3 text-green-500" />
                    ) : card.trend === "down" ? (
                      <IconTrendingDown className="h-3 w-3 text-red-500" />
                    ) : null}
                    <span className={card.trend === "up" ? "text-green-600" : card.trend === "down" ? "text-red-600" : ""}>
                      {card.change}
                    </span>
                    <span>from last period</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Chart and Top Performers */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            <Card className="col-span-4">
              <CardHeader>
                <CardTitle>Performance Overview</CardTitle>
                <CardDescription>QR scans over selected time period</CardDescription>
              </CardHeader>
              <CardContent className="pl-2">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <ChartAreaInteractive data={analytics} />
                )}
              </CardContent>
            </Card>
            <Card className="col-span-3">
              <CardHeader>
                <CardTitle>Top Campaigns</CardTitle>
                <CardDescription>Best performing QR campaigns</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : topCampaigns.length === 0 ? (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    No campaigns yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {topCampaigns.map((performer, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <div>
                            <p className="text-sm font-medium">{performer.name}</p>
                            <p className="text-xs text-muted-foreground">{performer.type}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{performer.value}</p>
                          <p className="text-xs text-green-600">{performer.change}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="engagement" className="space-y-4">
          {/* Engagement Metrics */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Avg. Engagement Rate</CardTitle>
                <CardDescription>User engagement percentage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {loading ? <IconLoader2 className="h-8 w-8 animate-spin" /> : `${avgEngagementRate.toFixed(1)}%`}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Based on {daysCount} days of data</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Avg. Session Length</CardTitle>
                <CardDescription>Time spent per session</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {loading ? <IconLoader2 className="h-8 w-8 animate-spin" /> : formatDuration(Math.round(avgSessionDurationSeconds))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Average over selected period</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Retention Rate</CardTitle>
                <CardDescription>Users returning within 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {loading ? <IconLoader2 className="h-8 w-8 animate-spin" /> : `${avgRetentionRate.toFixed(1)}%`}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Based on {daysCount} days of data</p>
              </CardContent>
            </Card>
          </div>

          {/* Hourly Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Hourly Activity</CardTitle>
              <CardDescription>Average user engagement throughout the day</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : hourlyData.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No hourly data available yet
                </div>
              ) : (
                <ChartContainer
                  config={hourlyChartConfig}
                  className="aspect-auto h-[300px] w-full"
                >
                  <BarChart data={hourlyData}>
                    <defs>
                      <linearGradient id="fillSessions" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#f97316"
                          stopOpacity={1}
                        />
                        <stop
                          offset="95%"
                          stopColor="#f97316"
                          stopOpacity={0.6}
                        />
                      </linearGradient>
                      <linearGradient id="fillUniqueUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#06b6d4"
                          stopOpacity={1}
                        />
                        <stop
                          offset="95%"
                          stopColor="#06b6d4"
                          stopOpacity={0.6}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="hour"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(value) => {
                        const hourNum = Number(value)
                        return isNaN(hourNum) ? String(value) : formatHour(hourNum)
                      }}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          labelFormatter={(value) => {
                            const hourNum = Number(value)
                            return isNaN(hourNum) ? String(value) : formatHour(hourNum)
                          }}
                          indicator="dot"
                        />
                      }
                    />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      height={36}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                    <Bar
                      dataKey="sessions"
                      fill="url(#fillSessions)"
                      name="Sessions"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="unique_users"
                      fill="url(#fillUniqueUsers)"
                      name="Players"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
